"use client";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import useMagicLink from "@/components/hooks/login/useMagicLink.hook";
import { Spinner } from "@/components/ui/spinner";
import { useTranslation } from "@/hooks/useTranslation.hook";

const magicLinkSchema = z.object({
  email: z.string().email("有効なメールアドレスを入力してください"),
});

interface MagicLinkFormData {
  email: string;
}

const MagicLinkForm = () => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const {
    onSubmit: requestLink,
    isLoading: requesting,
    recaptchaLoaded,
    errorMessage,
    setErrorMessage,
  } = useMagicLink();

  const form = useForm<MagicLinkFormData>({
    resolver: zodResolver(magicLinkSchema),
    defaultValues: {
      email: "",
    },
  });

  const onSubmit = async (values: MagicLinkFormData) => {
    setIsLoading(true);
    setErrorMessage(""); // Clear any previous error
    try {
      await requestLink(values);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoBack = () => {
    setErrorMessage("");
    form.reset();
  };

  // Show error div only when there's an error message
  if (errorMessage) {
    return (
      <div className="space-y-4">
        <div className="text-center space-y-2">
          <h3 className="text-lg font-semibold">エラーが発生しました。</h3>
          <p className="text-sm text-muted-foreground">{errorMessage}</p>
        </div>

        <Button
          type="button"
          variant="outline"
          className="w-full"
          onClick={handleGoBack}
        >
          戻る
        </Button>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-2 text-center mb-4 sm:mb-6">
        <h2 className="text-primary text-2xl sm:text-3xl lg:text-4xl font-semibold font-notoSans">
          アカウントにログイン
        </h2>
        <p className="text-sm sm:text-base text-muted-foreground">
          プロジェクト管理を始めましょう
        </p>
      </div>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="space-y-3 sm:space-y-4"
        >
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>メールアドレス</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    placeholder="demo@opentakt.com"
                    {...field}
                  />
                </FormControl>
                <FormMessage t={t} />
              </FormItem>
            )}
          />

          <Button
            type="submit"
            className="w-full"
            disabled={isLoading || requesting || !recaptchaLoaded}
          >
            {(isLoading || requesting) && (
              <Spinner size="sm" className="mr-2" />
            )}
            マジックリンク
          </Button>
        </form>
      </Form>
    </>
  );
};

export default MagicLinkForm;
